import React, { useEffect, useState } from "react";
import { X, Send, CheckCircle2, AlertCircle } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const INITIAL = { name: "", company: "", email: "", phone: "", message: "", website: "" };

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${BASE_URL}/api/public/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong.");
    }
  };

  const closeAndReset = () => {
    onClose();
    setTimeout(() => {
      setForm(INITIAL);
      setStatus("idle");
      setErrorMsg("");
    }, 250);
  };

  return (
    <div className="nxl-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeAndReset()}>
      <div className="nxl-modal" role="dialog" aria-modal="true" aria-label="Request a demo">
        <button className="nxl-modal-close" onClick={closeAndReset} aria-label="Close">
          <X size={20} />
        </button>

        {status === "success" ? (
          <div className="nxl-modal-result">
            <CheckCircle2 size={52} className="nxl-modal-ok" />
            <h3>Request sent!</h3>
            <p>Thank you — our team will contact you shortly to schedule your personalized demo.</p>
            <button className="nxl-btn nxl-btn-primary" onClick={closeAndReset}>Done</button>
          </div>
        ) : (
          <>
            <h3 className="nxl-modal-title">Request a Demo</h3>
            <p className="nxl-modal-sub">
              Tell us a little about your company and we'll schedule a personalized demo of NexPloy HRMS.
            </p>
            <form onSubmit={submit} className="nxl-modal-form">
              <div className="nxl-modal-grid">
                <label>
                  Name *
                  <input type="text" value={form.name} onChange={set("name")} required minLength={2} placeholder="Your full name" />
                </label>
                <label>
                  Company
                  <input type="text" value={form.company} onChange={set("company")} placeholder="Company name" />
                </label>
                <label>
                  Email *
                  <input type="email" value={form.email} onChange={set("email")} required placeholder="you@company.com" />
                </label>
                <label>
                  Phone *
                  <input type="tel" value={form.phone} onChange={set("phone")} required minLength={5} placeholder="+20 1x xxx xxxx" />
                </label>
              </div>
              <label>
                Message
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know (team size, current tools…)" />
              </label>
              {/* Honeypot — hidden from real users */}
              <input
                type="text" value={form.website} onChange={set("website")}
                name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
              />
              {status === "error" && (
                <div className="nxl-modal-error">
                  <AlertCircle size={16} />
                  <span>
                    {errorMsg} You can also email us directly at{" "}
                    <a href="mailto:support@nexploy-eg.com">support@nexploy-eg.com</a>.
                  </span>
                </div>
              )}
              <button type="submit" className="nxl-btn nxl-btn-primary nxl-btn-lg nxl-modal-submit" disabled={status === "sending"}>
                {status === "sending" ? "Sending…" : (<>Send Request <Send size={16} /></>)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
